import { GENERATED_ENGINE_CATALOG } from './generated/engine-catalog';

/**
 * PISO de versão do CLI Claude Code aceito quando o worker precisa DIRIGIR um
 * binário externo (Anthropic standalone / PATH / npm global). Alinhado à versão
 * de CLI que o SDK pinado ({@link CLAUDE_AGENT_SDK_VERSION}) carrega: dirigir um
 * CLI mais antigo que o runtime do SDK é o drift de protocolo que causa crash
 * nativo (SIGSEGV) — ver docs/projeto/lessons-learned-cli-signal-crash-retry.md.
 *
 * Mesmo acima do piso, "compatível" NÃO é "casado": a paridade real vem do
 * binário nativo do SDK (node_modules) ou do managed dir, cujo marcador
 * `.sdk-version` confere a identidade do SDK. O piso é a defesa do caminho
 * externo, onde não há marcador para conferir.
 *
 * Efeito: quando o worker VAI dirigir o binário (`pathToClaudeCodeExecutable`)
 * e ele está abaixo do piso, a sessão é RECUSADA com erro acionável em vez de
 * executar e arriscar o crash. Um externo que o SDK não vai usar (dev com
 * node_modules) não é barrado.
 *
 * Precedência em runtime, do mais forte para o mais fraco:
 *   1. `spassuCode.claude.minimumVersion` do app-config, propagado ao worker no
 *      handshake `authenticate` (`FrontierConfigs.claude.minimumVersion`);
 *   2. env `SPASSU_CLAUDE_MIN_VERSION` — escape hatch de operador, também lida
 *      pelo Rust do Desktop, que não recebe o handshake;
 *   3. este valor.
 *
 * O Tauri Rust (engines.rs) mantém uma const local sincronizada — há teste no
 * Rust que lê este valor direto do contract.ts e falha se divergir.
 */
export const CLAUDE_MINIMUM_VERSION = '2.1.156';

/**
 * Versão exata do `@anthropic-ai/claude-agent-sdk` adotada pelo projeto.
 *
 * Fonte única da verdade para a PARIDADE SDK↔CLI. O runtime do SDK só é
 * compatível com o binário nativo do CLI publicado na MESMA versão
 * (`@anthropic-ai/claude-agent-sdk-<plataforma>@<esta-versão>`). Dirigir um CLI
 * de versão diferente pode causar mismatch de protocolo de controle e crash
 * nativo (SIGSEGV) — ver docs/projeto/plano-adequacao-sdk-cli.md e
 * docs/projeto/lessons-learned-cli-signal-crash-retry.md.
 *
 * Onde é usada:
 *  - deve casar com o pin de `@anthropic-ai/claude-agent-sdk` no package.json
 *    do worker/backend/CLI (há teste de sync que falha se divergir);
 *  - o bootstrap do Worker Desktop (Tauri) baixa o tarball nativo desta versão
 *    na primeira execução, em vez do `latest` do instalador oficial.
 *
 * Ao bumpar o SDK: atualizar este valor E os três package.json juntos.
 */
export const CLAUDE_AGENT_SDK_VERSION = '0.3.156';

/**
 * Modelos Claude expostos pelo worker (`getAvailableModels`).
 * A disponibilidade real depende do CLI/conta; esta lista reflete os modelos
 * suportados no Claude Code 2.1.x adotado pelo projeto.
 *
 * Valor gerado a partir de `app-config.desktop.yaml` — para incluir ou remover
 * um modelo, edite o YAML e rode `node scripts/sync-engine-catalog.mjs`.
 */
export const CLAUDE_AVAILABLE_MODELS =
  GENERATED_ENGINE_CATALOG['claude-code'].models;

/**
 * Modelo padrão quando nenhum é especificado (worker `DEFAULT_MODEL` e backend
 * `FALLBACK_MODEL`). Não confundir com `spassuCode.models.default` do
 * app-config, que é o default de configuração de fases.
 */
export const CLAUDE_DEFAULT_MODEL =
  GENERATED_ENGINE_CATALOG['claude-code'].defaultModel;

/**
 * Modelos Codex expostos pelo worker (`LocalCodexSessionManager.getAvailableModels`)
 * e pelo catálogo do Worker Desktop. A disponibilidade real depende da
 * conta/auth (`OPENAI_API_KEY` ou `~/.codex/auth.json`). Ver
 * docs/projeto/plano-multi-engine.md.
 *
 * Valor gerado — ver {@link CLAUDE_AVAILABLE_MODELS}.
 */
export const CODEX_AVAILABLE_MODELS = GENERATED_ENGINE_CATALOG.codex.models;

/** Modelo Codex padrão quando nenhum é especificado. */
export const CODEX_DEFAULT_MODEL = GENERATED_ENGINE_CATALOG.codex.defaultModel;

/**
 * Versão exata do Codex CLI (`@openai/codex`) homologada pelo projeto. Fonte
 * única — o bootstrap do Worker Desktop baixa o tarball de plataforma desta
 * MESMA versão (`@openai/codex@<versão>-<plat>`, que embute
 * `vendor/<triple>/bin/codex(.exe)` + recursos irmãos) para
 * `<engines>/codex-<versão>/`, e o resolver do worker
 * (`codex-cli-resolver.ts`) procura exatamente esse diretório. Há teste de
 * sync no Rust do Desktop (`engines.rs`). Sem o managed (ou override
 * `SPASSU_CODEX_CLI_PATH`), o worker cai no `npx codex` (sem pin — dev via
 * terminal).
 */
export const CODEX_CLI_VERSION = '0.154.0';

/**
 * Versão exata do `@github/copilot-sdk` — o CLIENTE JavaScript que o worker
 * usa para falar com o runtime (DT-10 da iniciativa
 * `integracao-github-copilot`). Fonte única: deve casar com o pin no
 * package.json do worker (há teste de sync).
 *
 * NÃO é a versão do que o Worker Desktop baixa — ver
 * {@link COPILOT_CLI_VERSION}.
 */
export const COPILOT_SDK_VERSION = '1.0.13';

/**
 * Versão exata do pacote de plataforma `@github/copilot-<plat>` que o Worker
 * Desktop baixa para o managed dir.
 *
 * Por que a CLI e não o `@github/copilot-sdk-<plat>`: o pacote da CLI contém
 * TUDO o que o do SDK contém — `prebuilds/<plat>/copilot-runtime(.exe)` e
 * `runtime.node`, na mesma estrutura — MAIS o `copilot(.exe)` interativo, que
 * é o único caminho de login do Copilot (grava o estado em `~/.copilot`, lido
 * por `check_cli_login`). Baixar os dois traria o runtime duplicado, em
 * pacotes com versionamento independente.
 *
 * A compatibilidade de protocolo se mantém porque esta CLI embute o
 * `@github/copilot-sdk` da MESMA linha de {@link COPILOT_SDK_VERSION}; ao
 * bumpar, confira que isso continua valendo (o `copilot-sdk/index.js` dentro
 * do pacote cita a versão que ele empacota).
 */
export const COPILOT_CLI_VERSION = '1.0.83';

/**
 * Catálogo ESTÁTICO do GitHub Copilot: só `'auto'` (D-CMC-6, iniciativa
 * `copilot-modelos-da-conta`). `'auto'` delega a escolha ao Copilot e é o
 * fallback quando a conta não pode ser consultada. A lista real de modelos
 * vem da conta logada (`CopilotClient.listModels()` → `CopilotAccountModel`),
 * e ids do Copilot fora deste catálogo são aceitos (`isEngineModelAccepted`).
 *
 * Valor gerado — ver {@link CLAUDE_AVAILABLE_MODELS}.
 */
export const COPILOT_AVAILABLE_MODELS =
  GENERATED_ENGINE_CATALOG['github-copilot'].models;

/** Modelo Copilot padrão quando nenhum é especificado (D-GHC-4: 'auto'). */
export const COPILOT_DEFAULT_MODEL =
  GENERATED_ENGINE_CATALOG['github-copilot'].defaultModel;

/** Default efetivo do paralelismo de tasks. Mantido sequencial ate o merge seguro. */
export const DEFAULT_MAX_PARALLEL_TASKS = 1;

/** Teto defensivo para slots paralelos quando nao houver cap central menor. */
export const MAX_PARALLEL_TASKS_LIMIT = 16;

export * from './context-files';
export * from './copilot-models';
export * from './security-scanner';
export * from './task-breakdown-config';
export * from './verification';
export * from './working-branch';

export interface PersistedParallelTaskState {
  totalSlots?: number | null;
}

export function normalizeMaxParallelTasks(
  value: unknown,
  options: { max?: number } = {},
): number {
  const max = Number.isFinite(options.max)
    ? Math.max(DEFAULT_MAX_PARALLEL_TASKS, Math.floor(options.max as number))
    : MAX_PARALLEL_TASKS_LIMIT;
  const numericValue =
    typeof value === 'number' ? value : Number.parseFloat(String(value));

  if (
    !Number.isFinite(numericValue) ||
    numericValue < DEFAULT_MAX_PARALLEL_TASKS
  ) {
    return DEFAULT_MAX_PARALLEL_TASKS;
  }

  return Math.min(Math.floor(numericValue), max);
}

export function getPersistedMaxParallelTasks(
  tasks: PersistedParallelTaskState[],
): number {
  const maxTotalSlots = tasks.reduce((max, task) => {
    const totalSlots = task.totalSlots ?? DEFAULT_MAX_PARALLEL_TASKS;
    return Math.max(max, totalSlots);
  }, DEFAULT_MAX_PARALLEL_TASKS);

  return normalizeMaxParallelTasks(maxTotalSlots, {
    max: MAX_PARALLEL_TASKS_LIMIT,
  });
}

export function resolveEffectiveMaxParallelTasks(params: {
  requested?: unknown;
  fallbackMaxParallelTasks: number;
}): number {
  const operatorMaxParallelTasks = normalizeMaxParallelTasks(
    params.fallbackMaxParallelTasks,
    {
      max: MAX_PARALLEL_TASKS_LIMIT,
    },
  );

  if (params.requested === undefined) {
    return operatorMaxParallelTasks;
  }

  return normalizeMaxParallelTasks(params.requested, {
    max: operatorMaxParallelTasks,
  });
}

export function resolveResumeMaxParallelTasks(params: {
  requested?: unknown;
  persistedMaxParallelTasks: number;
  fallbackMaxParallelTasks: number;
}): number {
  if (params.requested !== undefined) {
    return resolveEffectiveMaxParallelTasks({
      requested: params.requested,
      fallbackMaxParallelTasks: params.fallbackMaxParallelTasks,
    });
  }

  const operatorMaxParallelTasks = normalizeMaxParallelTasks(
    params.fallbackMaxParallelTasks,
    {
      max: MAX_PARALLEL_TASKS_LIMIT,
    },
  );
  const persisted = normalizeMaxParallelTasks(
    params.persistedMaxParallelTasks,
    {
      max: operatorMaxParallelTasks,
    },
  );
  if (persisted > DEFAULT_MAX_PARALLEL_TASKS) {
    return persisted;
  }

  return DEFAULT_MAX_PARALLEL_TASKS;
}

/**
 * Nome do plugin de skills do Spassu Code, como declarado em
 * `.spassu/.claude-plugin/plugin.json` (`name`). O Claude Code expõe os
 * comandos de um plugin local NAMESPACED por esse nome — ex.: o arquivo
 * `commands/criar-prd.md` vira o slash command `/spassu-code:criar-prd`.
 *
 * Por isso o prompt enviado ao agente precisa invocar o comando namespaced
 * (`/spassu-code:criar-prd`), não o bare (`/criar-prd`) — senão o CLI não
 * reconhece o comando e encerra sem executar o skill (0 turnos). Ver
 * docs/projeto/lessons-learned-* e o runbook de validação.
 *
 * MANTER SINCRONIZADO com o `name` do `.claude-plugin/plugin.json`.
 */
export const CLAUDE_SKILLS_PLUGIN_NAME = 'spassu-code';

/**
 * Converte um slash command BARE (`/criar-prd`, `/executar-task`) no comando
 * NAMESPACED do plugin (`/spassu-code:criar-prd`), que é como o Claude Code
 * expõe comandos de um plugin local. Idempotente (não re-namespaceia).
 *
 * Use ao montar o PROMPT enviado ao agente. NÃO use para localizar o arquivo
 * `.md` do comando (esse usa o nome bare). Ver
 * docs/projeto/lessons-learned-plugin-slash-command-namespace.md.
 */
export function toPluginSlashCommand(skill: string): string {
  const bare = skill.replace(/^\//, '');
  if (bare.length === 0 || bare.includes(':')) {
    return skill;
  }
  return `/${CLAUDE_SKILLS_PLUGIN_NAME}:${bare}`;
}

// ---------------------------------------------------------------------------
// AI Engine — abstração de "motor de IA" que o worker e a CLI standalone
// podem usar. Decisão D0.1 em docs/worker-desktop/fase-0-pauta.md:
// usar `aiEngine` em vez de "provider" (que já significa coisas diferentes
// no projeto — git provider do ADR-049 e agent type da AgentKeysSettingsTab).
//
// Esta é a interface leve: metadados + capabilities + auth. Implementações
// concretas (LocalClaudeSessionManager, LocalCodexSessionManager,
// ClaudeCodeProvider/CodexProvider na CLI standalone) trazem o método de
// criar sessão com a assinatura que cada cenário precisa.
// ---------------------------------------------------------------------------

/** Engines suportados (D0.2; `github-copilot` desde a iniciativa `integracao-github-copilot`). */
export type AIEngineName = 'claude-code' | 'codex' | 'github-copilot';

/**
 * Capabilities por engine. Usadas para gates de fase (D0.4 — fases
 * skill-dependentes ficam bloqueadas em engines que não suportam skills
 * customizadas no v1).
 */
export interface AIEngineCapabilities {
  /** Suporta `.claude-plugin/plugin.json` (skills customizadas do Spassu Code). */
  supportsSkillsPlugin: boolean;
  /** Suporta servidor MCP customizado via SDK. */
  supportsMCP: boolean;
  /** Suporta tool use (function calling). */
  supportsToolUse: boolean;
  /** Stream incremental durante a resposta. */
  supportsStreaming: boolean;
}

/**
 * Modo de autenticação usado pela engine. Decide como o usuário "paga" pelo
 * consumo. Detectado em ordem de prioridade definida pelo session manager.
 */
export type AIEngineAuthMode = 'subscription' | 'api-key' | 'env';

/** Resultado de `isAuthenticated()` — informa o modo ativo para a UI mostrar. */
export interface AIEngineAuthStatus {
  authenticated: boolean;
  /** Qual fonte autenticou. `null` quando não autenticado. */
  activeMode: AIEngineAuthMode | null;
}

/** Identidade da engine — metadados que o backend e a UI consultam. */
export interface AIEngineMetadata {
  readonly name: AIEngineName;
  readonly capabilities: AIEngineCapabilities;
}

/**
 * Contrato leve que session managers implementam para serem reconhecíveis
 * como AIEngine. Foco: identidade + capabilities + status de auth.
 *
 * Operações específicas de cada cenário (`createSession()` na CLI standalone,
 * `startSession()` no worker) ficam na implementação concreta — não estão
 * no contract para evitar acoplamento entre dois mundos com lifecycles
 * diferentes (CLI é stateless-friendly; worker é stateful com sessions
 * concorrentes, pendingQuestions, recovery snapshots, etc).
 */
export interface AIEngine extends AIEngineMetadata {
  /**
   * Verifica se a engine está autenticada para uso. Implementações devem
   * checar na ordem: env var → keyring/token → estado OAuth do CLI nativo.
   * O `mode` opcional permite forçar uma fonte específica.
   */
  isAuthenticated(mode?: AIEngineAuthMode): Promise<AIEngineAuthStatus>;

  /** Modelos disponíveis para a engine. Pode bater na API ou retornar lista hardcoded. */
  getAvailableModels(): Promise<string[]>;
}

/**
 * Capabilities padrão para Claude Code. Implementações podem expor isso
 * direto via `capabilities` ao implementar `AIEngine`.
 */
export const CLAUDE_CODE_CAPABILITIES: AIEngineCapabilities = {
  supportsSkillsPlugin: true,
  supportsMCP: true,
  supportsToolUse: true,
  supportsStreaming: true,
};

/**
 * Capabilities padrão para Codex. Note `supportsSkillsPlugin: false` —
 * Codex CLI não consome `.claude-plugin/plugin.json` (D0.4). UI deve
 * bloquear fases skill-dependentes (`dividindo-tasks`, `executando`,
 * `pronto`) com aviso quando engine ativa é `codex`.
 */
export const CODEX_CAPABILITIES: AIEngineCapabilities = {
  supportsSkillsPlugin: false,
  supportsMCP: false,
  supportsToolUse: true,
  supportsStreaming: true,
};

/**
 * Capabilities do GitHub Copilot (via `@github/copilot-sdk`).
 *
 * `supportsSkillsPlugin: true` desde a ONDA 2 (iniciativa
 * `integracao-github-copilot`, DT-02/RF17): o Copilot não lê o formato
 * `.claude-plugin/plugin.json` — a skill de fase chega INLINE no
 * `customAgents[].prompt` (D-GHC-2, `skill-inline-loader`), com o mesmo
 * fail-fast e sinal de conformidade do caminho Claude. Semanticamente esta
 * capability significa "executa fases skill-dependentes", e é ela que libera
 * `dividindo-tasks`/`executando`/`pronto` nos gates (UI e dispatch). As
 * guardas de push/worktree (T-08), a política de tools por fase (T-09) e o
 * task loop (T-05/T-10) são pré-requisitos já entregues.
 * `supportsMCP: true` cobre tools customizadas in-process (`defineTool`) e
 * servidores MCP por sessão do SDK.
 */
export const GITHUB_COPILOT_CAPABILITIES: AIEngineCapabilities = {
  supportsSkillsPlugin: true,
  supportsMCP: true,
  supportsToolUse: true,
  supportsStreaming: true,
};

/** Lista de fases que dependem de skills customizadas (engine deve ter `supportsSkillsPlugin: true`). */
export const SKILL_DEPENDENT_PHASES: Phase[] = [
  'dividindo-tasks',
  'executando',
  'pronto',
];

/** Helper para gates de UI: dada engine + fase, retorna se pode executar. */
export function canEngineRunPhase(
  capabilities: AIEngineCapabilities,
  phase: Phase,
): boolean {
  if (
    SKILL_DEPENDENT_PHASES.includes(phase) &&
    !capabilities.supportsSkillsPlugin
  ) {
    return false;
  }
  return true;
}

/** Capabilities por NOME de engine — fonte única para resolver name→capabilities. */
export const AI_ENGINE_CAPABILITIES: Record<
  AIEngineName,
  AIEngineCapabilities
> = {
  'claude-code': CLAUDE_CODE_CAPABILITIES,
  codex: CODEX_CAPABILITIES,
  'github-copilot': GITHUB_COPILOT_CAPABILITIES,
};

/**
 * Type guard para validar strings vindas de payload/banco contra a lista de
 * engines conhecidas. Backend DEVE usar este helper na validação de payload em
 * vez de comparações literais por nome (gate por nome descarta engines novas
 * em silêncio — ver TechSpec da iniciativa `integracao-github-copilot`).
 */
export function isAIEngineName(value: unknown): value is AIEngineName {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(AI_ENGINE_CAPABILITIES, value)
  );
}

/**
 * Catálogo de modelos por engine — Record exaustivo de propósito (DT-03): ao
 * adicionar uma engine em `AIEngineName`, o compilador força a entrada aqui
 * (esquecer `*_AVAILABLE_MODELS` soltos era silencioso). Consumidores
 * (worker `getAvailableModels`, backend `getModelConfig`, UI) devem resolver
 * modelos por este mapa, nunca por constante de engine específica.
 */
export const AI_ENGINE_MODEL_CATALOG: Record<
  AIEngineName,
  { readonly models: readonly string[]; readonly defaultModel: string }
> = {
  'claude-code': {
    models: CLAUDE_AVAILABLE_MODELS,
    defaultModel: CLAUDE_DEFAULT_MODEL,
  },
  codex: {
    models: CODEX_AVAILABLE_MODELS,
    defaultModel: CODEX_DEFAULT_MODEL,
  },
  'github-copilot': {
    models: COPILOT_AVAILABLE_MODELS,
    defaultModel: COPILOT_DEFAULT_MODEL,
  },
};

/**
 * Gate por NOME de engine — conveniência para o frontend, que só conhece o
 * nome (`activity.aiEngine`) e não a instância. Resolve as capabilities pelo
 * mapa e delega a `canEngineRunPhase`. NÃO duplicar `SKILL_DEPENDENT_PHASES`
 * no frontend; usar este helper.
 */
export function canEngineNameRunPhase(
  engine: AIEngineName,
  phase: Phase,
): boolean {
  return canEngineRunPhase(AI_ENGINE_CAPABILITIES[engine], phase);
}

export type Phase =
  | 'gerando-prd'
  | 'gerando-techspec'
  | 'dividindo-tasks'
  | 'executando'
  | 'qa'
  | 'pronto'
  | 'finalizado';

/** Única via de execução do produto; mantido como tipo para a coluna `work_path`. */
export type ActivityWorkPath = 'frontier-web';

export type SkillOrigin =
  | 'spassu'
  | 'provider-native'
  | 'user-custom'
  | 'community'
  | 'unknown';

export type PhaseTransitionTrigger =
  | 'manual_approval'
  | 'agent_approval'
  | 'execution_complete';

export interface ApprovalPolicy {
  mode: 'manual' | 'agent' | 'hybrid' | 'none';
  requiresHumanApproval: boolean;
  agentToolEnabled: boolean;
}

export interface PhaseActionPolicy {
  canStart: boolean;
  canApprove: boolean;
  canExecuteTasks: boolean;
  canPauseResumeExecution: boolean;
  canRetryTask: boolean;
}

export type AutoStartPolicy = 'none' | 'backend' | 'frontend';

export type PhaseSessionLifecycle =
  | 'pauses_live'
  | 'ends_after_run'
  | 'no_agent_session';

export type PhaseConformityRecoveryMode =
  | 'revalidate_artifact'
  | 'rerun_phase'
  | 'revalidate_delivery'
  // `reset_pr` nunca é política de fase: é um recoveryAction derivado apenas no
  // summary quando a fase `pronto` está em `PR_INCONSISTENT` (pr_url de outro
  // repositório). Habilita a ação humana "Refazer PR" (limpa a pr_url) em vez da
  // reconciliação de `revalidate_delivery`, que falharia para esse estado.
  | 'reset_pr'
  | 'replan_tasks'
  | 'none';

export interface PhaseConformityPolicy {
  sessionLifecycle: PhaseSessionLifecycle;
  recoveryMode: PhaseConformityRecoveryMode;
}

export interface PipelinePhaseDefinition {
  id: Phase;
  label: string;
  skill: string | null;
  artifactType: string | null;
  approvalPolicy: ApprovalPolicy;
  autoStart: boolean;
  autoStartPolicy: AutoStartPolicy;
  sessionLifecycle: PhaseSessionLifecycle;
  conformityRecoveryMode: PhaseConformityRecoveryMode;
  actions: PhaseActionPolicy;
}

export interface PhaseTransitionRule {
  from: Phase;
  to: Phase;
  trigger: PhaseTransitionTrigger;
  autoTransition: boolean;
}

export interface PipelineDefinition {
  version: string;
  phases: PipelinePhaseDefinition[];
  transitionRules: PhaseTransitionRule[];
  terminalPhases: Phase[];
}

export const PHASES_ORDER: Phase[] = [
  'gerando-prd',
  'gerando-techspec',
  'dividindo-tasks',
  'executando',
  'qa',
  'pronto',
  'finalizado',
];

const PHASE_SET = new Set<Phase>(PHASES_ORDER);
const TASK_NUMBER_PATTERN = /^\d+(?:\.\d+)*$/;

export const DEFAULT_PIPELINE_DEFINITION: PipelineDefinition = {
  version: '2026-07-16',
  phases: [
    {
      id: 'gerando-prd',
      label: 'PRD',
      skill: '/criar-prd',
      artifactType: 'prd',
      approvalPolicy: {
        mode: 'manual',
        requiresHumanApproval: true,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'pauses_live',
      conformityRecoveryMode: 'revalidate_artifact',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'gerando-techspec',
      label: 'TechSpec',
      skill: '/criar-techspec',
      artifactType: 'techspec',
      approvalPolicy: {
        mode: 'manual',
        requiresHumanApproval: true,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'pauses_live',
      conformityRecoveryMode: 'revalidate_artifact',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'dividindo-tasks',
      label: 'Tasks',
      skill: '/criar-tasks',
      artifactType: 'tasks',
      approvalPolicy: {
        mode: 'manual',
        requiresHumanApproval: true,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'pauses_live',
      conformityRecoveryMode: 'revalidate_artifact',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'executando',
      label: 'Código',
      skill: '/executar-task',
      artifactType: null,
      approvalPolicy: {
        mode: 'none',
        requiresHumanApproval: false,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'no_agent_session',
      conformityRecoveryMode: 'replan_tasks',
      actions: {
        canStart: false,
        canApprove: false,
        canExecuteTasks: true,
        canPauseResumeExecution: true,
        canRetryTask: true,
      },
    },
    {
      id: 'qa',
      label: 'Testes',
      skill: '/executar-qa',
      artifactType: 'test-report',
      approvalPolicy: {
        mode: 'agent',
        requiresHumanApproval: false,
        agentToolEnabled: true,
      },
      autoStart: true,
      autoStartPolicy: 'backend',
      sessionLifecycle: 'ends_after_run',
      conformityRecoveryMode: 'rerun_phase',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'pronto',
      label: 'PR',
      skill: '/criar-pr',
      artifactType: null,
      approvalPolicy: {
        mode: 'hybrid',
        requiresHumanApproval: true,
        agentToolEnabled: true,
      },
      autoStart: true,
      autoStartPolicy: 'frontend',
      sessionLifecycle: 'ends_after_run',
      conformityRecoveryMode: 'revalidate_delivery',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'finalizado',
      label: 'Finalizado',
      skill: null,
      artifactType: null,
      approvalPolicy: {
        mode: 'none',
        requiresHumanApproval: false,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'no_agent_session',
      conformityRecoveryMode: 'none',
      actions: {
        canStart: false,
        canApprove: false,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
  ],
  transitionRules: [
    {
      from: 'gerando-prd',
      to: 'gerando-techspec',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'gerando-techspec',
      to: 'dividindo-tasks',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'dividindo-tasks',
      to: 'executando',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'executando',
      to: 'qa',
      trigger: 'execution_complete',
      autoTransition: true,
    },
    {
      from: 'executando',
      to: 'qa',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'qa',
      to: 'pronto',
      trigger: 'agent_approval',
      autoTransition: true,
    },
    {
      from: 'qa',
      to: 'pronto',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'pronto',
      to: 'finalizado',
      trigger: 'manual_approval',
      autoTransition: false,
    },
  ],
  terminalPhases: ['finalizado'],
};

export function clonePipelineDefinition(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): PipelineDefinition {
  return {
    ...definition,
    phases: definition.phases.map(phase => ({
      ...phase,
      approvalPolicy: { ...phase.approvalPolicy },
      actions: { ...phase.actions },
    })),
    transitionRules: definition.transitionRules.map(rule => ({ ...rule })),
    terminalPhases: [...definition.terminalPhases],
  };
}

export function getDefaultPipelineDefinition(): PipelineDefinition {
  return clonePipelineDefinition(DEFAULT_PIPELINE_DEFINITION);
}

export function isValidPhase(phase: string): phase is Phase {
  return PHASE_SET.has(phase as Phase);
}

export function getPhaseOrder(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): Phase[] {
  return definition.phases.map(phase => phase.id);
}

export function getPhaseDefinition(
  phase: Phase,
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): PipelinePhaseDefinition | undefined {
  return definition.phases.find(item => item.id === phase);
}

export function getPhaseConformityPolicy(
  phase: Phase,
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): PhaseConformityPolicy | undefined {
  const phaseDefinition = getPhaseDefinition(phase, definition);
  return phaseDefinition
    ? {
        sessionLifecycle: phaseDefinition.sessionLifecycle,
        recoveryMode: phaseDefinition.conformityRecoveryMode,
      }
    : undefined;
}

export function resolvePhaseConformityPolicy(
  phaseDefinition: PipelinePhaseDefinition,
): PhaseConformityPolicy {
  return {
    sessionLifecycle: phaseDefinition.sessionLifecycle,
    recoveryMode: phaseDefinition.conformityRecoveryMode,
  };
}

export function getTransitionRule(
  from: Phase,
  trigger: PhaseTransitionTrigger,
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): PhaseTransitionRule | undefined {
  return definition.transitionRules.find(
    rule => rule.from === from && rule.trigger === trigger,
  );
}

export function getPhaseLabels(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): Record<Phase, string> {
  return definition.phases.reduce<Record<Phase, string>>((acc, phase) => {
    acc[phase.id] = phase.label;
    return acc;
  }, {} as Record<Phase, string>);
}

export function getApprovablePhases(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): Phase[] {
  return definition.phases
    .filter(phase => phase.actions.canApprove)
    .map(phase => phase.id);
}

export function getStartablePhases(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): Phase[] {
  return definition.phases
    .filter(phase => phase.actions.canStart)
    .map(phase => phase.id);
}

export function canonicalizeTaskNumber(taskNumber: string): string {
  const normalized = taskNumber.trim();
  if (!normalized || !TASK_NUMBER_PATTERN.test(normalized)) {
    return normalized;
  }

  const segments = normalized
    .split('.')
    .map(segment => Number.parseInt(segment, 10))
    .filter(segment => !Number.isNaN(segment));

  if (segments.length === 0) {
    return normalized;
  }

  while (segments.length > 1 && segments[segments.length - 1] === 0) {
    segments.pop();
  }

  return segments.join('.');
}

export function compareTaskNumbers(a: string, b: string): number {
  const canonicalA = canonicalizeTaskNumber(a);
  const canonicalB = canonicalizeTaskNumber(b);
  const aValid = TASK_NUMBER_PATTERN.test(canonicalA);
  const bValid = TASK_NUMBER_PATTERN.test(canonicalB);

  if (aValid && bValid) {
    const aSegments = canonicalA
      .split('.')
      .map(segment => Number.parseInt(segment, 10));
    const bSegments = canonicalB
      .split('.')
      .map(segment => Number.parseInt(segment, 10));
    const maxLength = Math.max(aSegments.length, bSegments.length);

    for (let index = 0; index < maxLength; index += 1) {
      const aValue = aSegments[index] ?? 0;
      const bValue = bSegments[index] ?? 0;
      if (aValue !== bValue) {
        return aValue - bValue;
      }
    }
  } else if (aValid !== bValid) {
    return aValid ? -1 : 1;
  }

  return canonicalA.localeCompare(canonicalB);
}

// Provider-agnostic SCM types (multi-provider git)
export * from './scm';
export * from './boards';

// Worker protocol types
export * from './worker-protocol';
export * from './phase-conformity';
export * from './phase-conformity-heading-match';
export * from './phase-artifact-path';
